import React, { useEffect, useState } from 'react';
import { Drawer, Box, Typography, TextField, Button, IconButton, FormControl, InputLabel, Select, MenuItem, FormHelperText, Divider, useTheme, useMediaQuery, InputAdornment, CircularProgress, Tooltip, Alert, Fade, Autocomplete, ListItemIcon, ListItemText, InputBase, Chip, Stack, Checkbox } from '@mui/material';
import { Icon } from '@iconify/react';
import { VALID_MTOS, type ValidMTO, VALID_SUB_AGENTS } from '../utils/constants';
// Country data with flags, ISO codes, and dial codes
const COUNTRIES = [{
  name: 'United Arab Emirates',
  iso: 'AE',
  dialCode: '+971',
  flag: '🇦🇪',
  phoneFormat: '(+971) 5X XXX XXXX'
}, {
  name: 'Saudi Arabia',
  iso: 'SA',
  dialCode: '+966',
  flag: '🇸🇦',
  phoneFormat: '(+966) 5X XXX XXXX'
}, {
  name: 'United Kingdom',
  iso: 'GB',
  dialCode: '+44',
  flag: '🇬🇧',
  phoneFormat: '(+44) 7XXXX XXXXXX'
}, {
  name: 'United States',
  iso: 'US',
  dialCode: '+1',
  flag: '🇺🇸',
  phoneFormat: '(+1) XXX XXX XXXX'
}, {
  name: 'Qatar',
  iso: 'QA',
  dialCode: '+974',
  flag: '🇶🇦',
  phoneFormat: '(+974) XXXX XXXX'
}, {
  name: 'Kuwait',
  iso: 'KW',
  dialCode: '+965',
  flag: '🇰🇼',
  phoneFormat: '(+965) XXXX XXXX'
}, {
  name: 'Bahrain',
  iso: 'BH',
  dialCode: '+973',
  flag: '🇧🇭',
  phoneFormat: '(+973) XXXX XXXX'
}, {
  name: 'Oman',
  iso: 'OM',
  dialCode: '+968',
  flag: '🇴🇲',
  phoneFormat: '(+968) XXXX XXXX'
}, {
  name: 'Pakistan',
  iso: 'PK',
  dialCode: '+92',
  flag: '🇵🇰',
  phoneFormat: '(+92) 3XX XXXXXXX'
}];
// Integration modes
const INTEGRATION_MODES = [{
  value: 'push',
  label: 'Push'
}, {
  value: 'pull',
  label: 'Pull'
}, {
  value: 'file',
  label: 'File'
}];
// Transaction types
const TRANSACTION_TYPES = [{
  value: 'otc',
  label: 'OTC',
  tooltip: 'Over The Counter'
}, {
  value: 'ft',
  label: 'FT',
  tooltip: 'Funds Transfer'
}, {
  value: 'ibft',
  label: 'IBFT',
  tooltip: 'Interbank Funds Transfer'
}];
interface PartnerFormProps {
  open: boolean;
  partner: any | null;
  onClose: () => void;
  onSave: (partnerData: any) => void;
}
interface FormData {
  partnerName: ValidMTO | string;
  country: string;
  countryObject: any | null;
  address: string;
  pointOfContact: string;
  contactNumber: string;
  settlementAccount: string;
  accountTitle?: string;
  integrationMode: string;
  transactionsAllowed: string[];
  configuredSubagents: string[];
  email?: string;
}
interface FormErrors {
  partnerName?: string;
  country?: string;
  address?: string;
  pointOfContact?: string;
  contactNumber?: string;
  settlementAccount?: string;
  integrationMode?: string;
  transactionsAllowed?: string;
  configuredSubagents?: string;
  email?: string;
}
// IBAN validation regex
const GENERAL_IBAN_REGEX = /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/;
const PAKISTAN_IBAN_REGEX = /^PK\d{2}[A-Z0-9]{20}$/;
// Email validation regex
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const PartnerFormDrawer: React.FC<PartnerFormProps> = ({
  open,
  partner,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState<FormData>({
    partnerName: '',
    country: '',
    countryObject: null,
    address: '',
    pointOfContact: '',
    contactNumber: '',
    settlementAccount: '',
    accountTitle: '',
    integrationMode: '',
    transactionsAllowed: [],
    configuredSubagents: [],
    email: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isFetchingTitle, setIsFetchingTitle] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [showAccountTitle, setShowAccountTitle] = useState(false);
  const [ibanValid, setIbanValid] = useState(false);
  const [phoneFieldFocused, setPhoneFieldFocused] = useState(false);
  const [countrySearchText, setCountrySearchText] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  useEffect(() => {
    if (partner) {
      // Find country object from name
      const countryObj = COUNTRIES.find(c => c.name === partner.country) || null;
      setFormData({
        partnerName: partner.partnerName || '',
        country: partner.country || '',
        countryObject: countryObj,
        address: partner.address || '',
        pointOfContact: partner.pointOfContact || '',
        contactNumber: partner.contactNumber || '',
        settlementAccount: partner.settlementAccount || '',
        accountTitle: partner.accountTitle || '',
        integrationMode: partner.integrationMode || '',
        transactionsAllowed: partner.transactionsAllowed || [],
        configuredSubagents: partner.configuredSubagents || [],
        email: partner.email || ''
      });
      // If partner has account title, show the field
      if (partner.accountTitle) {
        setShowAccountTitle(true);
        validateIban(partner.settlementAccount || '');
      }
    } else {
      setFormData({
        partnerName: '',
        country: '',
        countryObject: null,
        address: '',
        pointOfContact: '',
        contactNumber: '',
        settlementAccount: '',
        accountTitle: '',
        integrationMode: '',
        transactionsAllowed: [],
        configuredSubagents: [],
        email: ''
      });
      setShowAccountTitle(false);
    }
    setErrors({});
    setFetchError(null);
  }, [partner, open]);
  // Effect to prefill dial code when country changes and phone field is empty
  useEffect(() => {
    if (formData.countryObject && (!formData.contactNumber || formData.contactNumber === '' || formData.contactNumber.startsWith('+') && formData.contactNumber.length <= 4)) {
      setFormData(prev => ({
        ...prev,
        contactNumber: formData.countryObject.dialCode + ' '
      }));
    }
  }, [formData.countryObject]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | {
    name?: string;
    value: unknown;
  }>) => {
    const {
      name,
      value
    } = e.target;
    if (name) {
      // Special handling for settlementAccount (IBAN)
      if (name === 'settlementAccount') {
        // Format IBAN: remove spaces/dashes, convert to uppercase
        const formattedValue = String(value).replace(/[\s-]/g, '').toUpperCase();
        setFormData({
          ...formData,
          [name]: formattedValue,
          // Clear account title if IBAN is edited after a successful fetch
          ...(showAccountTitle && {
            accountTitle: ''
          })
        });
        // Validate the IBAN and update the button state
        validateIban(formattedValue);
        // If user changes IBAN after fetching title, reset the title
        if (showAccountTitle) {
          setShowAccountTitle(false);
          setFetchError(null);
        }
      } else {
        setFormData({
          ...formData,
          [name]: value
        });
      }
      // Clear error when field is edited
      if (errors[name as keyof FormErrors]) {
        setErrors({
          ...errors,
          [name]: undefined
        });
      }
    }
  };
  const handleCountryChange = (event: any, newValue: any) => {
    setFormData({
      ...formData,
      country: newValue ? newValue.name : '',
      countryObject: newValue,
      // Clear phone number if country changes to avoid validation issues
      ...(formData.contactNumber && {
        contactNumber: newValue ? newValue.dialCode + ' ' : ''
      })
    });
    // Clear country error if exists
    if (errors.country) {
      setErrors({
        ...errors,
        country: undefined
      });
    }
  };
  const handleTransactionsAllowedChange = (event: React.ChangeEvent<{
    value: unknown;
  }>) => {
    const value = event.target.value as string[];
    setFormData({
      ...formData,
      transactionsAllowed: value
    });
    // Clear error if at least one transaction type is selected
    if (errors.transactionsAllowed && value.length > 0) {
      setErrors({
        ...errors,
        transactionsAllowed: undefined
      });
    }
  };
  const handleSubagentsChange = (event: any, newValue: string[]) => {
    setFormData({
      ...formData,
      configuredSubagents: newValue
    });
    // Clear error if at least one subagent is selected
    if (errors.configuredSubagents && newValue.length > 0) {
      setErrors({
        ...errors,
        configuredSubagents: undefined
      });
    }
  };
  const handlePhoneFocus = () => {
    setPhoneFieldFocused(true);
    // Prefill dial code if field is empty and country is selected
    if ((!formData.contactNumber || formData.contactNumber === '') && formData.countryObject) {
      setFormData({
        ...formData,
        contactNumber: formData.countryObject.dialCode + ' '
      });
    }
  };
  const handlePhoneBlur = () => {
    setPhoneFieldFocused(false);
  };
  const validateIban = (iban: string): boolean => {
    // Check if it's a valid IBAN format
    const isGeneralValid = GENERAL_IBAN_REGEX.test(iban);
    // If it starts with PK, apply the Pakistan-specific rules
    const isPakistanIban = iban.startsWith('PK');
    const isPakistanValid = isPakistanIban ? PAKISTAN_IBAN_REGEX.test(iban) : true;
    const isValid = isGeneralValid && isPakistanValid;
    setIbanValid(isValid);
    // Set error message if invalid
    if (iban && !isValid) {
      setErrors({
        ...errors,
        settlementAccount: isPakistanIban ? 'Pakistan IBAN must be exactly 24 characters (PK + 22 characters)' : 'Enter a valid IBAN (e.g., PK36BIPL...)'
      });
    } else {
      // Clear error if valid or empty
      const updatedErrors = {
        ...errors
      };
      delete updatedErrors.settlementAccount;
      setErrors(updatedErrors);
    }
    return isValid;
  };
  const handleFetchTitle = () => {
    setIsFetchingTitle(true);
    setFetchError(null);
    // Simulate API call with timeout
    setTimeout(() => {
      // 90% success rate for demo purposes
      const isSuccess = Math.random() < 0.9;
      if (isSuccess) {
        // Success case
        const mockTitles = ['ACME CORPORATION LTD.', 'GLOBAL EXCHANGE SERVICES', 'FIRST TRADING COMPANY', 'INTERNATIONAL REMITTANCE LLC', 'UNITED FINANCIAL SERVICES'];
        const randomTitle = mockTitles[Math.floor(Math.random() * mockTitles.length)];
        setFormData({
          ...formData,
          accountTitle: randomTitle
        });
        setShowAccountTitle(true);
        // Show toast (in a real app, you would use a toast library)
        console.log('Account title fetched successfully');
      } else {
        // Error case
        setFetchError('Title fetch failed. Please verify IBAN or try again.');
      }
      setIsFetchingTitle(false);
    }, 2500); // 2.5 seconds delay
  };
  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.partnerName.trim()) {
      newErrors.partnerName = 'Partner name (MTO) is required';
    }
    if (!formData.country) {
      newErrors.country = 'Country is required';
    }
    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }
    if (!formData.pointOfContact.trim()) {
      newErrors.pointOfContact = 'Point of contact is required';
    }
    if (!formData.settlementAccount.trim()) {
      newErrors.settlementAccount = 'Settlement account is required';
    } else if (!validateIban(formData.settlementAccount)) {
      newErrors.settlementAccount = formData.settlementAccount.startsWith('PK') ? 'Pakistan IBAN must be exactly 24 characters (PK + 22 characters)' : 'Enter a valid IBAN (e.g., PK36BIPL...)';
    }
    // Email validation (if provided)
    if (formData.email && !EMAIL_REGEX.test(formData.email)) {
      newErrors.email = 'Enter a valid email address';
    }
    // Integration Mode validation
    if (!formData.integrationMode) {
      newErrors.integrationMode = 'Integration mode is required';
    }
    // Transactions Allowed validation
    if (!formData.transactionsAllowed.length) {
      newErrors.transactionsAllowed = 'At least one transaction type is required';
    }
    // Configured Subagents validation
    if (!formData.configuredSubagents.length) {
      newErrors.configuredSubagents = 'At least one subagent is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = () => {
    if (validate()) {
      onSave({
        ...formData,
        id: partner?.id
      });
    }
  };
  return <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{
    sx: {
      width: {
        xs: '100%',
        sm: 400
      },
      p: 0
    }
  }} data-id="element-1569" data-mp="3oc6eaxzc73fw">
      <Box sx={{
      p: 3
    }} data-id="element-1570" data-mp="1ys6ijlaz0ro0">
        <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3
      }} data-id="element-1571" data-mp="23ue1wvy4ra0d">
          <Typography variant="h6" data-id="element-1572" data-mp="2djntmap2aqny">
            {partner ? 'Edit Partner' : 'Add New Partner'}
          </Typography>
          <IconButton onClick={onClose} size="small" data-id="element-1573" data-mp="2ih7738dxqzeu">
            <Icon icon="lucide:x" width={18} height={18} data-id="element-1574" data-mp="2iklcyqlt5qr1" />
          </IconButton>
        </Box>
        <Box component="form" noValidate autoComplete="off" sx={{
        mt: 2
      }} data-id="element-1575" data-mp="2mloox20qj2mz">
          {/* Partner Name (MTO) Dropdown */}
          <FormControl fullWidth margin="normal" error={!!errors.partnerName} required data-id="element-1576" data-mp="2wfxcdbaws5ea">
            <InputLabel id="partner-name-label" data-id="element-1577" data-mp="1237c8bty5zyg">Partner Name (MTO)</InputLabel>
            <Select labelId="partner-name-label" name="partnerName" value={formData.partnerName} onChange={handleChange} label="Partner Name (MTO)" data-id="element-1578" data-mp="2skug9svqsau6">
              {VALID_MTOS.map(mto => <MenuItem key={mto} value={mto} data-id="element-1579" data-mp="31jg34088yc0r">
                  {mto}
                </MenuItem>)}
            </Select>
            {errors.partnerName && <FormHelperText data-id="element-1580" data-mp="29uusrafjv3sv">{errors.partnerName}</FormHelperText>}
          </FormControl>
          {/* Integration Mode Dropdown */}
          <FormControl fullWidth margin="normal" error={!!errors.integrationMode} required data-id="element-1581" data-mp="2imofxfpl3crf">
            <InputLabel id="integration-mode-label" data-id="element-1582" data-mp="2duwv7lt40197">
              Integration Mode
            </InputLabel>
            <Select labelId="integration-mode-label" name="integrationMode" value={formData.integrationMode} onChange={handleChange} label="Integration Mode" data-id="element-1583" data-mp="1q4j1zrysfwd1">
              {INTEGRATION_MODES.map(mode => <MenuItem key={mode.value} value={mode.value} data-id="element-1584" data-mp="1gt4u924kkd8v">
                  {mode.label}
                </MenuItem>)}
            </Select>
            {errors.integrationMode && <FormHelperText data-id="element-1585" data-mp="172a961327jp0">{errors.integrationMode}</FormHelperText>}
          </FormControl>
          {/* Transactions Allowed Multi-select */}
          <FormControl fullWidth margin="normal" error={!!errors.transactionsAllowed} required data-id="element-1586" data-mp="1zqxtnufzi51i">
            <InputLabel id="transactions-allowed-label" data-id="element-1587" data-mp="1kvrnn7gw2686">
              <Box sx={{
              display: 'flex',
              alignItems: 'center'
            }} data-id="element-1588" data-mp="1pr6zjtvc5s1y">
                Transactions Allowed
                <Tooltip title="Select transaction types that this partner can process" data-id="element-1589" data-mp="eeeaiqtxjhpl">
                  <Icon icon="lucide:help-circle" width={16} height={16} style={{
                  marginLeft: '4px',
                  opacity: 0.7
                }} data-id="element-1590" data-mp="2h09yrvmzxvsl" />
                </Tooltip>
              </Box>
            </InputLabel>
            <Select labelId="transactions-allowed-label" multiple name="transactionsAllowed" value={formData.transactionsAllowed} onChange={handleTransactionsAllowedChange} label="Transactions Allowed" renderValue={selected => <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5
          }} data-id="element-1592" data-mp="1jci0szg46qjy">
                  {(selected as string[]).map(value => {
              const transaction = TRANSACTION_TYPES.find(t => t.value === value);
              return <Chip key={value} label={transaction?.label || value} size="small" sx={{
                backgroundColor: 'rgba(14, 165, 233, 0.1)',
                color: '#0EA5E9',
                borderRadius: '4px',
                fontWeight: 600,
                fontSize: '0.7rem'
              }} data-id="element-1593" data-mp="3q83elc3fn0li" />;
            })}
                </Box>} data-id="element-1591" data-mp="228zxlv9tlgn2">
              {TRANSACTION_TYPES.map(type => <MenuItem key={type.value} value={type.value} data-id="element-1594" data-mp="3h9i0pu8wo9r2">
                  <Checkbox checked={formData.transactionsAllowed.indexOf(type.value) > -1} data-id="element-1595" data-mp="rjo2njfwtf4g" />
                  <ListItemText primary={<Tooltip title={type.tooltip} data-id="element-1597" data-mp="1hma08h0l8uf8">
                        <span data-id="element-1598" data-mp="1cnow46i7tqli">{type.label}</span>
                      </Tooltip>} data-id="element-1596" data-mp="1hlqppld9p8b8" />
                </MenuItem>)}
            </Select>
            {errors.transactionsAllowed && <FormHelperText data-id="element-1599" data-mp="1mjii0ghtqwfy">{errors.transactionsAllowed}</FormHelperText>}
          </FormControl>
          {/* Configured Subagents Multi-select with Search */}
          <FormControl fullWidth margin="normal" error={!!errors.configuredSubagents} required data-id="element-1600" data-mp="1wboarsfwhhw7">
            <Autocomplete multiple id="configured-subagents" options={[...VALID_SUB_AGENTS]} value={formData.configuredSubagents} onChange={handleSubagentsChange} disableCloseOnSelect getOptionLabel={option => option} renderOption={(props, option, {
            selected
          }) => <li {...props} data-id="element-1602" data-mp="2mn02d1gp54xm">
                  <Checkbox checked={selected} sx={{
              marginRight: 1
            }} data-id="element-1603" data-mp="2rjqfph149mfc" />
                  <ListItemText primary={option} data-id="element-1604" data-mp="2ct10m3kjbn67" />
                </li>} renderTags={(value, getTagProps) => value.map((option, index) => <Chip {...getTagProps({
            index
          })} label={option} size="small" sx={{
            backgroundColor: '#F1F5F9',
            color: '#334155',
            borderRadius: '4px',
            fontWeight: 500,
            fontSize: '0.75rem'
          }} data-id="element-1605" data-mp="23t497u6rga7g" data-mpi={index} />)} renderInput={params => <TextField {...params} label="Configured Subagents" placeholder="Search and select subagents" required error={!!errors.configuredSubagents} helperText={errors.configuredSubagents || 'Select subagents that can process transactions for this partner'} data-id="element-1606" data-mp="10odxcla2yxl8" />} data-id="element-1601" data-mp="2a8bfwii0bed7" />
          </FormControl>
          {/* Enhanced Country Selection with Flags and Search */}
          <FormControl fullWidth margin="normal" error={!!errors.country} required data-id="element-1607" data-mp="1obm2923lk4mq">
            <Autocomplete id="country-select" options={COUNTRIES} value={formData.countryObject} onChange={handleCountryChange} getOptionLabel={option => option.name} isOptionEqualToValue={(option, value) => option.iso === value.iso} renderOption={(props, option) => <MenuItem {...props} sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }} data-id="element-1609" data-mp="3nknfk9e5p5up">
                  <Box component="span" sx={{
              fontSize: '1.2rem',
              mr: 1
            }} data-id="element-1610" data-mp="fphmfkutu6l">
                    {option.flag}
                  </Box>
                  <ListItemText primary={option.name} secondary={`(${option.iso}) ${option.dialCode}`} primaryTypographyProps={{
              fontWeight: 500
            }} secondaryTypographyProps={{
              fontSize: '0.75rem'
            }} data-id="element-1611" data-mp="34scg6pipnq1c" />
                </MenuItem>} renderInput={params => <TextField {...params} label="Country" required error={!!errors.country} helperText={errors.country} InputProps={{
            ...params.InputProps,
            startAdornment: <>
                        {formData.countryObject && <InputAdornment position="start" data-id="element-1613" data-mp="660gtwtu0srj">
                            <Typography variant="body1" sx={{
                  fontSize: '1.2rem',
                  mr: 1
                }} data-id="element-1614" data-mp="b2h4yr11774m">
                              {formData.countryObject.flag}
                            </Typography>
                          </InputAdornment>}
                        {params.InputProps.startAdornment}
                      </>,
            endAdornment: <>
                        {params.InputProps.endAdornment}
                        <InputAdornment position="end" data-id="element-1615" data-mp="378uq7kjqbdi2">
                          <Icon icon="lucide:search" width={16} height={16} color="#64748B" data-id="element-1616" data-mp="3779uhrlohoim" />
                        </InputAdornment>
                      </>
          }} data-id="element-1612" data-mp="p2igr1iu3yyv" />} data-id="element-1608" data-mp="1fc8ss9fh3g20" />
          </FormControl>
          <TextField fullWidth margin="normal" label="Address" name="address" value={formData.address} onChange={handleChange} error={!!errors.address} helperText={errors.address} required multiline rows={2} data-id="element-1617" data-mp="2oaid5npp5jh7" />
          <TextField fullWidth margin="normal" label="Point of Contact" name="pointOfContact" value={formData.pointOfContact} onChange={handleChange} error={!!errors.pointOfContact} helperText={errors.pointOfContact} required data-id="element-1618" data-mp="3ae95b0ahxp7c" />
          {/* Email Input with validation */}
          <TextField fullWidth margin="normal" label="Email" name="email" type="email" value={formData.email || ''} onChange={handleChange} error={!!errors.email} helperText={errors.email} placeholder="contact@example.com" data-id="element-1619" data-mp="2rnrptxeocq3x" />
          {/* Enhanced Phone Input with Country-based Formatting */}
          <Tooltip title={formData.countryObject ? `Format: ${formData.countryObject.phoneFormat}` : 'Select a country for format guidance'} placement="top" arrow data-id="element-1620" data-mp="28odqt1238p6k">
            <TextField fullWidth margin="normal" label="Contact Number" name="contactNumber" value={formData.contactNumber} onChange={handleChange} onFocus={handlePhoneFocus} onBlur={handlePhoneBlur} error={!!errors.contactNumber} helperText={errors.contactNumber || 'Format updates with selected country'} required placeholder={formData.countryObject ? formData.countryObject.phoneFormat : 'Select a country first'} InputProps={{
            startAdornment: formData.countryObject && phoneFieldFocused ? <InputAdornment position="start" data-id="element-1622" data-mp="2rq8ln8g53j05">
                      <Typography variant="body2" color="text.secondary" data-id="element-1623" data-mp="2rnm72ouu038f">
                        {formData.countryObject.flag}
                      </Typography>
                    </InputAdornment> : null
          }} data-id="element-1621" data-mp="1p33rh0whxuc9" />
          </Tooltip>
          {/* IBAN Settlement Account Field Group */}
          <Box sx={{
          mt: 3,
          mb: 2,
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'flex-end',
          gap: isMobile ? 2 : 1
        }} data-id="element-1624" data-mp="3dhjfqh7xpahs">
            <TextField fullWidth label="BIPL Settlement Account (IBAN)" name="settlementAccount" value={formData.settlementAccount} onChange={handleChange} error={!!errors.settlementAccount} helperText={errors.settlementAccount || 'Enter a valid IBAN'} placeholder="e.g., PK36BIPL0000000000000000" required disabled={isFetchingTitle} InputProps={{
            sx: {
              textTransform: 'uppercase'
            }
          }} sx={{
            flex: isMobile ? 1 : 3,
            width: isMobile ? '100%' : 'auto',
            '& .MuiOutlinedInput-root': {
              '&:hover .MuiOutlinedInput-notchedOutline': {
                boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)'
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#0EA5E9',
                boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)'
              }
            }
          }} data-id="element-1625" data-mp="3rhkr4qlts0bi" />
            <Tooltip title="Validate and fetch account title" data-id="element-1626" data-mp="o6d1560ujofb">
              <span data-id="element-1627" data-mp="3dw6syz391mth">
                <Button variant="outlined" disabled={!ibanValid || isFetchingTitle} onClick={handleFetchTitle} sx={{
                minWidth: '120px',
                width: isMobile ? '100%' : 'auto',
                height: '56px',
                borderColor: '#D1D5DB',
                color: '#0EA5E9',
                '&:hover': {
                  borderColor: '#0EA5E9',
                  backgroundColor: 'rgba(14, 165, 233, 0.04)',
                  transform: 'translateY(-1px)'
                },
                '&.Mui-disabled': {
                  borderColor: '#E5E7EB'
                }
              }} aria-live="polite" data-id="element-1628" data-mp="3dxhzv7rzraot">
                  {isFetchingTitle ? <>
                      <CircularProgress size={16} sx={{
                    mr: 1,
                    color: '#0EA5E9'
                  }} data-id="element-1629" data-mp="1qsoz1u5qzs78" />
                      Fetching...
                    </> : 'Fetch Title'}
                </Button>
              </span>
            </Tooltip>
          </Box>
          {/* Fetch error message */}
          {fetchError && <Fade in={!!fetchError} data-id="element-1630" data-mp="hjaiu7gbga8j">
              <Alert severity="error" icon={<Icon icon="lucide:alert-circle" width={18} height={18} data-id="element-1632" data-mp="cmao2sox2twq" />} sx={{
            mb: 2
          }} onClose={() => setFetchError(null)} data-id="element-1631" data-mp="hmeufpw42898">
                {fetchError}
              </Alert>
            </Fade>}
          {/* Account Title Field (shown only after successful fetch) */}
          {showAccountTitle && <Fade in={showAccountTitle} data-id="element-1633" data-mp="3q2i3o02vg4zo">
              <TextField fullWidth margin="normal" label="Account Title" value={formData.accountTitle || ''} disabled InputProps={{
            readOnly: true,
            endAdornment: <InputAdornment position="end" data-id="element-1635" data-mp="1exenn6tt0wyy">
                      <Icon icon="lucide:check-circle" width={18} height={18} color="#10B981" data-id="element-1636" data-mp="1ezr57ivnh5bh" />
                    </InputAdornment>
          }} sx={{
            mb: 2,
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: '#0F172A',
              fontWeight: 500
            },
            '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
              borderColor: '#D1D5DB',
              borderStyle: 'dashed'
            }
          }} data-id="element-1634" data-mp="19b8gi4972fqq" />
            </Fade>}
        </Box>
      </Box>
      <Box sx={{
      position: 'sticky',
      bottom: 0,
      backgroundColor: 'background.paper',
      p: 3,
      borderTop: '1px solid #E5E7EB'
    }} data-id="element-1637" data-mp="2ovnuo0kq07wm">
        <Box sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 2
      }} data-id="element-1638" data-mp="2xwca8zprigbm">
          <Button variant="outlined" onClick={onClose} data-id="element-1639" data-mp="32wgoej376yyo">
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} data-id="element-1640" data-mp="37tgyd5364lv2">
            Save
          </Button>
        </Box>
      </Box>
    </Drawer>;
};
export default PartnerFormDrawer;